import React, {useRef, useState, useEffect, useContext} from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import Loading from '../../../Loading/loading';
import '../settings.css';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import CustomDropdown from '../../../custom/customDropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { AuthContext } from '../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const roleLabel = (roleId) => {
  const map = {
    1: 'Direktor', 2: 'Administrativni radnik', 3: 'Knjigovođa',
    4: 'Prodaja', 5: 'Dispečer', 6: 'Vozač', 7: 'Magacioner', 8: 'Serviser'
  };
  return map[roleId] || '-';
};

const OperateriTab = () => {
  const { t } = useTranslation();
  const company     = useSelector(state => state.company);
  const userRole    = useSelector(state => state.user_role);
  const user        = useSelector(state => state.user);
  const toast       = useRef(null);
  const auth        = useContext(AuthContext);

  const [load, setLoad]               = useState(true);
  const [users, setUsers]             = useState([]);
  const [codes, setCodes]             = useState([]);
  const [render, setRender]           = useState(false);
  const [rola, setRola]               = useState([]);
  const [dropRola, setDropRola]       = useState(null);
  const [companyOptions, setCompanyOptions] = useState(null);
  const [dropCompany, setDropCompany] = useState(null);
  const [status, setStatus]           = useState(null);
  const [open, setOpen]               = useState(false);
  const [userSelect, setUserSelect]   = useState(null);
  const [selectStatus, setSelectStatus] = useState(false);
  const [masterRole, setMasterRole]   = useState(null);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [first, setFirst]             = useState(0);
  const [permMatrix, setPermMatrix]   = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [savingPerm, setSavingPerm]   = useState(false);

  // ── Mobilni pristup (Deo P) — operater dobija username+PIN za TMS_Mobile app ──
  const [mobPristupList, setMobPristupList] = useState([]);
  const [mobUsername, setMobUsername]       = useState('');
  const [mobPin, setMobPin]                 = useState('');
  const [showMobPin, setShowMobPin]         = useState(false);
  const [savingMobPristup, setSavingMobPristup] = useState(false);
  // Deo Q — operater koji je ujedno i zaposleni-vozač (Radno mesto ili Dodatna funkcija):
  // povezivanje sa njegovim zaposleni-zapisom otvara vozačke akcije na mobilnom preko
  // OVOG istog naloga, bez potrebe za drugim (MOBILNI OPERATERI) nalogom.
  const [zaposleniZaPovezivanje, setZaposleniZaPovezivanje] = useState([]);
  const [mobIdZaposleni, setMobIdZaposleni] = useState(null);
  const mobPristup = userSelect ? mobPristupList.find(m => m.id_operater === userSelect.id) : null;

  const renderFunction = () => setRender(prev => !prev);

  // ── Prava pristupa (Faza 1) ─────────────────────────────────
  const fetchPermissions = (idUser) => {
    setPermLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/users/permissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id_user: idUser }),
    })
      .then(handleErrors)
      .then(data => setPermMatrix(data || []))
      .catch(() => setPermMatrix([]))
      .finally(() => setPermLoading(false));
  };

  // Hijerarhija: brisanje ⊆ ažuriranje ⊆ pristup
  const togglePerm = (idModul, field) => {
    setPermMatrix(prev => prev.map(m => {
      if (m.id_modul !== idModul) return m;
      let pristup = !!m.pristup, azuriranje = !!m.azuriranje, brisanje = !!m.brisanje;
      if (field === 'pristup') {
        pristup = !pristup;
        if (!pristup) { azuriranje = false; brisanje = false; }
      } else if (field === 'azuriranje') {
        azuriranje = !azuriranje;
        if (azuriranje) pristup = true; else brisanje = false;
      } else {
        brisanje = !brisanje;
        if (brisanje) { pristup = true; azuriranje = true; }
      }
      return { ...m, pristup: pristup ? 1 : 0, azuriranje: azuriranje ? 1 : 0, brisanje: brisanje ? 1 : 0 };
    }));
  };

  const savePermissions = () => {
    if (!userSelect) return;
    setSavingPerm(true);
    const prava = permMatrix.map(m => ({
      id_modul: m.id_modul,
      pristup: m.pristup ? 1 : 0,
      azuriranje: m.azuriranje ? 1 : 0,
      brisanje: m.brisanje ? 1 : 0,
    }));
    fetch(process.env.REACT_APP_BACKEND_URL + '/users/permissions/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id_user: userSelect.id, prava }),
    })
      .then(handleErrors)
      .then(() => toast.current.show({ severity: 'success', summary: t('admin.m_sacuvano'), detail: t('admin.m_prava_sacuvana'), life: 3000 }))
      .catch(error => error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 })))
      .finally(() => setSavingPerm(false));
  };

  useEffect(() => {
    const requestOptionsGET = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
    };
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ firma_id: company.companyId }),
    };
    fetch(process.env.REACT_APP_BACKEND_URL + '/users/config', requestOptions)
      .then(handleErrors)
      .then(result => {
        setUsers(result.users);
        setCompanyOptions(result.companies);
        setCodes(result.codes || []);
        setLoad(false);
      })
      .catch(error => {
        error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 }));
        setLoad(false);
      });
    fetch(process.env.REACT_APP_BACKEND_URL + '/roles', requestOptionsGET)
      .then(handleErrors)
      .then(result => setRola(result))
      .catch(error => error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 })));
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/operateriPristup', requestOptions)
      .then(handleErrors)
      .then(result => setMobPristupList(result || []))
      .catch(() => {}); // opcioni podatak — tiho zanemari ako padne (npr. bez ADMINISTRACIJA prava)
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/zaposleniZaPovezivanje', requestOptions)
      .then(handleErrors)
      // Uvek NIZ: ako server vrati objekat (npr. poruku o gresci), Dropdown bi
      // dobio nesto sto ne ume da iscrta i ne bi se otvorio.
      .then(result => {
        const spisak = Array.isArray(result) ? result : [];
        setZaposleniZaPovezivanje(spisak);
        /* Ako izabrani zaposleni nije u spisku (spisak prazan, zaposleni ugasen ili
           prebacen u drugu firmu), izbor se PONISTAVA. Inace dropdown pokazuje
           placeholder kao da nista nije izabrano, a snimanje bi svejedno poslalo
           starog zaposlenog — vezalo bi operatera za nekog koga uopste ne vidi. */
        setMobIdZaposleni(prev => (prev && !spisak.some(z => z.value === prev) ? null : prev));
      })
      // Ranije je greska bila progutana praznim catch-om, pa je spisak ostajao
      // prazan bez ijednog traga zasto. Sada bar zavrsi u konzoli.
      .catch(err => { console.warn('Spisak zaposlenih za povezivanje nije učitan:', err); setZaposleniZaPovezivanje([]); });
    if (userRole !== null) setMasterRole(userRole.roleId);
  }, [render, open]); // eslint-disable-line

  const handleErrors = (response) => {
    if (response.ok) return response.json();
    throw response;
  };

  const handleOpenWindow  = () => setOpen(true);
  const handleCloseWindow = () => { setOpen(false); setUserSelect(null); setPermMatrix([]); };

  const handleSelectUser = (item) => {
    setUserSelect(item);
    setSelectStatus(item.status == 1);
    setStatus(null);
    setDropCompany(item.firma_id);
    setDropRola({ id: item.role_id, role: roleLabel(item.role_id) });
    setRender(false);
    fetchPermissions(item.id);
    const postojeciMob = mobPristupList.find(m => m.id_operater === item.id);
    setMobUsername(postojeciMob?.username || '');
    setMobPin('');
    setShowMobPin(false);
    /* Preuzima se SAMO zaposleni koji je stvarno vezan za OVOG operatera i koji
       postoji u ponudjenom spisku. Bez druge provere bi na operateru bez veze
       ostao zaposleni izabran kod prethodnog operatera. */
    const vezaniZaposleni = postojeciMob?.id_zaposleni || null;
    setMobIdZaposleni(
      vezaniZaposleni && zaposleniZaPovezivanje.some(z => z.value === vezaniZaposleni)
        ? vezaniZaposleni
        : null
    );
  };

  const handleSaveMobPristup = () => {
    if (!userSelect || !mobUsername) return;
    if (!mobPristup && !mobPin) {
      toast.current.show({ severity: 'warn', summary: t('admin.m_upozorenje'), detail: t('admin.mobPristupPinObavezan'), life: 3000 });
      return;
    }
    if (mobPin && !/^\d{4}$/.test(mobPin)) {
      toast.current.show({ severity: 'warn', summary: t('admin.m_upozorenje'), detail: t('admin.mobPristupPinFormat'), life: 3000 });
      return;
    }
    setSavingMobPristup(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/operaterPristup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id_operater: userSelect.id, username: mobUsername, pin: mobPin || undefined, id_zaposleni: mobIdZaposleni || null }),
    })
      .then(handleErrors)
      .then(data => { toast.current.show({ severity: 'success', summary: t('admin.m_uspesno'), detail: data.message, life: 4000 }); setMobPin(''); renderFunction(); })
      .catch(error => error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 })))
      .finally(() => setSavingMobPristup(false));
  };

  const handleToggleMobStatus = (newStatus) => {
    if (!userSelect) return;
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/operaterPristup/status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id_operater: userSelect.id, status: newStatus ? 1 : 0 }),
    })
      .then(handleErrors)
      .then(data => { toast.current.show({ severity: 'success', summary: t('admin.m_uspesno'), detail: data.message, life: 3000 }); renderFunction(); })
      .catch(error => error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 })));
  };


  const handleDBInput = () => {
    const newStatus = status !== null ? (status ? 1 : 0) : (selectStatus ? 1 : 0);
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id: userSelect.id, role_id: dropRola.id, company_id: dropCompany, status: newStatus, master: masterRole, user_id: user.userId }),
    };
    fetch(process.env.REACT_APP_BACKEND_URL + '/users', requestOptions)
      .then(handleErrors)
      .then(data => { toast.current.show({ severity: 'success', summary: t('admin.m_uspesno_azuriranje'), detail: data.message, life: 3000 }); setOpen(false); setUserSelect(null); renderFunction(); })
      .catch(error => error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 })));
  };

  const exportExcelUsers = async () => {
    if (!users.length) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Korisnici');
    const headers   = ['Rb', 'Firma', 'Ime', 'Prezime', 'Email', 'Rola', 'Status'];
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    users.forEach((u, i) => {
      const row = worksheet.addRow([i + 1, u.firma_naziv, u.name, u.surname, u.email, roleLabel(u.role_id), u.status == 1 ? 'Aktivan' : 'Neaktivan']);
      row.eachCell(cell => { cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; });
    });
    worksheet.columns.forEach(col => { col.width = 22; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'Korisnici.xlsx');
  };

  const usersHeader = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcelUsers} data-pr-tooltip="XLS" />
      </div>
      <input
        type="search"
        onInput={(e) => setGlobalFilter(e.target.value)}
        placeholder={t('nova.phPretraga')}
        className="p-inputtext p-component"
      />
    </div>
  );

  return (
    <div id="setting_main">
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="setting_table">
        <Card>
          <Tooltip target=".export-buttons>button" position="bottom" />
          <DataTable className="stavke-tabela"
            value={users}
            paginator
            rows={10}
            first={first}
            onPage={(e) => setFirst(e.first)}
            globalFilter={globalFilter}
            scrollable
            scrollHeight="400px"
            header={usersHeader}
            emptyMessage="Nema korisnika u bazi, kontaktirajte administratora!"
            resizableColumns
            columnResizeMode="expand"
            rowClassName={(row) => userSelect && row.id === userSelect.id ? 'selected-row' : ''}
          >
            <Column header={t('admin.rb')} body={(_, opts) => opts.rowIndex + 1} style={{ minWidth: '60px' }} />
            <Column field="firma_naziv" header={t('magacin.firma')} sortable style={{ minWidth: '120px' }} />
            <Column field="name" header={t('login.ime')} sortable style={{ minWidth: '100px' }} />
            <Column field="surname" header={t('login.prezime')} sortable style={{ minWidth: '100px' }} />
            <Column field="email" header={t('login.email')} sortable style={{ minWidth: '160px' }} />
            <Column header={t('admin.rola')} body={(row) => roleLabel(row.role_id)} sortable sortField="role_id" style={{ minWidth: '150px' }} />
            <Column
              header={t('nova.status')}
              body={(row) => (
                <span style={{ color: row.status == 1 ? 'green' : 'red', fontWeight: 600 }}>
                  {row.status == 1 ? 'Aktivan' : 'Neaktivan'}
                </span>
              )}
              style={{ minWidth: '100px' }}
            />
            <Column
              header={t('nova.otvori')}
              body={(row) => (
                <Button
                  icon="pi pi-pencil"
                  type="button"
                  className="p-button-text p-button-sm"
                  onClick={() => { handleSelectUser(row); handleOpenWindow(); }}
                />
              )}
              style={{ minWidth: '80px' }}
            />
          </DataTable>
        </Card>

        {codes.length > 0 && (
          <Card style={{ marginTop: '1rem'}} className='regKodoviTabela'>
            <h4 style={{ margin: '0 0 0.8rem 0', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Registracioni kodovi
            </h4>
            <DataTable
              value={codes.map((c, i) => ({ ...c, rb: i + 1 }))}
              scrollable
              emptyMessage="Nema registracionih kodova"
              resizableColumns
              columnResizeMode="expand"
            >
              <Column field="rb" header={t('admin.rb')} style={{ minWidth: '60px' }} />
              <Column field="firma_naziv" header={t('magacin.firma')} style={{ minWidth: '150px' }} />
              <Column
                header={t('login.regKod')}
                body={(row) => (
                  <code style={{ background: '#f4f4f4', padding: '2px 7px', borderRadius: '4px', letterSpacing: '1px' }}>
                    {row.SubscriptionCode}
                  </code>
                )}
                style={{ minWidth: '200px' }}
              />
            </DataTable>
          </Card>
        )}
      </div>

      <div id="setting_second">
        <Dialog
          visible={open && !!userSelect}
          onHide={handleCloseWindow}
          header={userSelect ? `${userSelect.name} ${userSelect.surname}` : ''}
          style={{ width: '580px' }}
          breakpoints={{ '768px': '95vw' }}
          modal
          draggable={false}
          resizable={false}
          footer={
            <div className="fakture-stavke-radnje">
              <Button onClick={savePermissions} loading={savingPerm} label={t('admin.sacuvajPrava')} icon="pi pi-shield" className="p-button p-button-raised p-button-help" />
              <Button onClick={handleSaveMobPristup} loading={savingMobPristup} label={t('admin.mobPristupSacuvaj')} icon="pi pi-mobile" className="p-button p-button-raised p-button-warning" />
              <Button onClick={handleDBInput} label={t('admin.sacuvaj')} className="p-button p-button-raised p-button-success" />
            </div>
          }
        >
          {userSelect && (
            <div className="card-body">
              <div style={{ margin: '1%', display: 'flex', width: '100%' }}>
                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('magacin.firma')}:</p>
                <Dropdown optionLabel="label" value={dropCompany} options={companyOptions} onChange={(e) => setDropCompany(e.value)} placeholder={t('admin.phFirmuKorisnika')} />
              </div>
              <div style={{ margin: '1%', display: 'flex', width: '100%' }}>
                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.ulogaKorisnika')}:</p>
                <Dropdown optionLabel="role" value={dropRola} options={rola} onChange={(e) => setDropRola(e.value)} placeholder={t('admin.phUloguKorisnika')} />
              </div>
              <div style={{ margin: '1%', display: 'flex', width: '100%' }}>
                <p style={{ margin: '0 5% 0 0', width: '150px' }}>{t('admin.nalogKorisnika')}:</p>
                <InputSwitch style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'start' }} checked={status == null ? selectStatus : status} onChange={(e) => setStatus(e.value)} />
              </div>

              {/* ── Prava pristupa po modulima ── */}
              <div className="perm-section">
                <div className="perm-title">{t('admin.pravaPoModulima')}</div>
                {dropRola?.id === 1 && (
                  <div className="perm-note">{t('admin.direktorSvaPrava')}</div>
                )}
                {permLoading ? (
                  <div className="perm-loading">{t('admin.ucitavamPrava')}</div>
                ) : (
                  <table className="perm-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>{t('admin.modul')}</th>
                        <th className="perm-col">{t('admin.pristup')}</th>
                        <th className="perm-col">{t('admin.azuriranje')}</th>
                        <th className="perm-col">{t('admin.brisanje')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permMatrix.map(m => (
                        <tr key={m.id_modul}>
                          <td style={{ textAlign: 'left' }}>{m.naziv}</td>
                          <td className="perm-col"><Checkbox checked={!!m.pristup}    onChange={() => togglePerm(m.id_modul, 'pristup')} /></td>
                          <td className="perm-col"><Checkbox checked={!!m.azuriranje} onChange={() => togglePerm(m.id_modul, 'azuriranje')} /></td>
                          <td className="perm-col"><Checkbox checked={!!m.brisanje}   onChange={() => togglePerm(m.id_modul, 'brisanje')} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* ── Mobilni pristup (Deo P) — operater dobija username+PIN za TMS_Mobile app ── */}
              <div className="perm-section">
                <div className="perm-title">{t('admin.mobPristupNaslov')}</div>
                <p className="perm-note" style={{ marginBottom: '10px' }}>{t('admin.mobPristupObjasnjenje')}</p>

                <div style={{ margin: '1% 0', display: 'flex', width: '100%', alignItems: 'center' }}>
                  <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.mobOpUsername')}:</p>
                  {/* Pretrazivac ovde vidi tekstualno polje + polje tipa password i
                      zakljuci da je to prijava, pa upisuje TVOJE sacuvane kredencijale
                      preko podataka izabranog operatera. Zato ime polja namerno ne lici
                      na "username" i autoComplete je iskljucen. */}
                  <InputText
                    name="mob-pristup-korisnik"
                    autoComplete="off"
                    spellCheck={false}
                    value={mobUsername}
                    onChange={(e) => setMobUsername(e.target.value)}
                    placeholder={t('admin.mobOpUsername')}
                    style={{ flex: 1 }}
                  />
                </div>
                <div style={{ margin: '1% 0', display: 'flex', width: '100%', alignItems: 'center' }}>
                  <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>
                    {mobPristup ? t('admin.mobOpResetPin') : t('admin.mobOpPin')}:
                  </p>
                  <div style={{ display: 'flex', flex: 1, gap: '0.4rem' }}>
                    {/* "new-password" je jedina vrednost koju Chrome zaista postuje za
                        polje tipa password — sa "off" i dalje nudi sacuvanu lozinku.
                        PIN se NIKAD ne prikazuje: postojeci se ne cita iz baze (cuva se
                        kao hash), a polje se prazni pri svakom otvaranju. */}
                    <InputText
                      type={showMobPin ? 'text' : 'password'}
                      name="mob-pristup-tajna"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={mobPin}
                      maxLength={4}
                      onChange={(e) => setMobPin(e.target.value.replace(/\D/g, ''))}
                      placeholder={mobPristup ? t('admin.mobOpOstaviPrazno') : t('admin.mobOpPin')}
                      style={{ flex: 1 }}
                    />
                    <Button type="button" icon={`pi ${showMobPin ? 'pi-eye-slash' : 'pi-eye'}`} className="p-button-text p-button-sm" onClick={() => setShowMobPin(p => !p)} />
                  </div>
                </div>
                <div style={{ margin: '1% 0', display: 'flex', width: '100%', alignItems: 'center' }}>
                  <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.mobPristupPovezaniZaposleni')}:</p>
                  <CustomDropdown
                    style={{ flex: 1 }}
                    options={zaposleniZaPovezivanje}
                    optionLabel="label"
                    value={mobIdZaposleni}
                    onChange={(e) => setMobIdZaposleni(e.target.value)}
                    placeholder={t('admin.mobPristupPovezaniZaposleniPlaceholder')}
                    filterPlaceholder={t('common.pretraga')}
                    /* Bez ove poruke prazan spisak izgleda kao da se dropdown
                       "ne otvara" — panel se otvori, ali je potpuno prazan. */
                    emptyMessage={t('admin.mobPristupNemaZaposlenih')}
                  />
                </div>
                <p className="perm-note" style={{ marginBottom: '10px' }}>{t('admin.mobPristupPovezaniZaposleniObjasnjenje')}</p>

                {/* Status i uparenost su dva kratka podatka o istom nalogu — stoje u
                    JEDNOM redu, odvojeni crtom, umesto da svaki uzima ceo red. */}
                {mobPristup && (
                  <div className="mob-pristup-stanje">
                    <span className="mob-pristup-stavka">
                      <span className="mob-pristup-naziv">{t('nova.status')}:</span>
                      <InputSwitch
                        checked={mobPristup.status == 1}
                        onChange={(e) => handleToggleMobStatus(e.value)}
                      />
                    </span>

                    <span className="mob-pristup-crta" aria-hidden="true">|</span>

                    <span className="mob-pristup-stavka">
                      <span className="mob-pristup-naziv">{t('admin.mobOpUredjajUpareno')}:</span>
                      <span style={{ fontWeight: 600, color: mobPristup.upareno == 1 ? 'green' : '#888' }}>
                        {mobPristup.upareno == 1 ? t('common.da') : t('common.ne')}
                      </span>
                      {/* Bez otparivanja: uparivanje prati prijavu. */}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default OperateriTab;
